import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Header } from '../../components';
import { customStyles } from '../../Utils/utils';
import BackButton from '../../components/BackButton';
import AddDistributionDialog, { DistributionPatientInfo } from './AddDistributionDialog';
import { fetchPatientDetails, extractPatientDemographics } from '../reports/helper';
import { HL7 } from '../../constant';
import { getToken, getHospitalId } from '../../Utils/authUtils';
import { getDistributionHistoryByStudyApi, DistributionRecord } from '../../rtk-apis/hl7/getDistributionHistory';
import { sendDraftDistributionApi } from '../../rtk-apis/hl7/sendDraftDistribution';
import { deleteDraftDistributionApi } from '../../rtk-apis/hl7/deleteDraftDistribution';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import '../../styled/pages/dashboard.scss';

// Walk a ProseMirror doc and extract the value cell adjacent to "Referred by:" label
function extractReferredByFromTemplateHTML(doc: any): string | null {
  if (!doc) return null;
  const template = typeof doc === 'string' ? (() => { try { return JSON.parse(doc); } catch { return null; } })() : doc;
  if (!template) return null;

  function allText(node: any): string {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    return (node.content || []).map(allText).join('');
  }

  function findInNode(node: any): string | null {
    if (!node) return null;
    if (node.type === 'tableRow' && Array.isArray(node.content)) {
      for (let i = 0; i < node.content.length - 1; i++) {
        const label = allText(node.content[i]).trim().replace(/\s+/g, ' ').toLowerCase();
        if (label.includes('referred by')) {
          const val = allText(node.content[i + 1]).trim();
          if (val) return val;
        }
      }
    }
    for (const child of node.content || []) {
      const result = findInNode(child);
      if (result) return result;
    }
    return null;
  }

  return findInNode(template);
}

export const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  ACCEPTED_BY_MO: 'Accepted by MO',
  DELIVERED: 'Delivered to Site',
  FILE_CONSUMED: 'File Processed',
  PRACTICE_ACK: 'Acknowledged by Software',
  DELIVERY_FAILED: 'Delivery Failed',
  PRACTICE_REJECTED: 'Rejected by Software',
  FAILED: 'Failed',
  NOT_ROUTABLE: 'Not Routable',
  ACK_ERROR: 'ACK Error',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#a78bfa',
  SENT: '#4ade80',
  ACCEPTED_BY_MO: '#60a5fa',
  DELIVERED: '#4ade80',
  FILE_CONSUMED: '#4ade80',
  PRACTICE_ACK: '#22c55e',
  DELIVERY_FAILED: '#f87171',
  PRACTICE_REJECTED: '#f87171',
  FAILED: '#f87171',
  NOT_ROUTABLE: '#fbbf24',
  ACK_ERROR: '#fb923c',
};

const DistributionPage = () => {
  const { studyInstanceUID } = useParams<{ studyInstanceUID: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const state = location.state as { studyData?: any; reportId?: string } | undefined;
  const navState = state?.studyData;

  // The report fetched below. Router state is a convenience, not a source of truth:
  // it carries whatever the previous screen happened to put in it, and two of the
  // screens that link here (the report list, and the legacy table) send no status at
  // all — so the page decided a finalized report was a draft and refused to send it.
  // A refresh or a pasted URL has no router state whatsoever. The fetch is the source;
  // router state only fills the gap while it is in flight.
  const [reportDoc, setReportDoc] = useState<any>(null);
  const studyData = reportDoc ? { ...navState, ...reportDoc } : navState;

  // Stored title-cased ('Finalized'), but the preview endpoints compare uppercase, so
  // do not depend on either casing surviving.
  const reportStatus: string | undefined =
    reportDoc?.reportStatus ?? reportDoc?.status ?? navState?.status;
  const isReportFinalized = String(reportStatus || '').toUpperCase() === 'FINALIZED';

  const [referredBy, setReferredBy] = useState<string | undefined>(undefined);
  const [reportId, setReportId] = useState<string | undefined>(undefined);
  const [reportAssignedTo, setReportAssignedTo] = useState<string | undefined>(navState?.reportAssignedTo);

  // Built from the report once it arrives, so the dialog opens with real values even
  // when there is no router state at all.
  const patientInfoForDialog: DistributionPatientInfo | undefined = studyData
    ? {
        name: studyData.name ?? studyData.patientName,
        patientName: studyData.patientName ?? studyData.name,
        dob: studyData.dob,
        sex: studyData.sex,
        age: studyData.age,
        reportName: studyData.reportName,
        date: studyData.date,
        time: studyData.time,
        studyDate: studyData.studyDate,
        studyTime: studyData.studyTime,
        modality: studyData.modality,
        patientId: studyData.patientId ?? studyData.mrn,
        reportAssignedTo,
        status: reportStatus,
        referredBy,
      }
    : undefined;
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [distributions, setDistributions] = useState<DistributionRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [sendingDraftId, setSendingDraftId] = useState<string | null>(null);
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editDistribution, setEditDistribution] = useState<DistributionRecord | null>(null);

  // Fetch referring GP name from PACS DICOM metadata
  useEffect(() => {
    if (!studyInstanceUID) return;
    fetchPatientDetails(studyInstanceUID).then(data => {
      const demographics = extractPatientDemographics(data);
      if (demographics?.referred_by) setReferredBy(demographics.referred_by);
    }).catch(() => {
      // PACS fetch failed — referredBy stays undefined
    });
  }, [studyInstanceUID]);

  // Fetch report _id from the HL7 backend (MongoDB) — not from production (CouchDB)
  useEffect(() => {
    if (!studyInstanceUID) return;
    const fetchReportId = async () => {
      try {
        const token = getToken();
        const hospitalId = getHospitalId();
        const res = await fetch(HL7.GET_CURRENT_REPORT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ studyInstanceUID, hospitalId }),
        });
        if (res.ok) {
          const json = await res.json();
          const reportData = json?.data || json?.result?.data || json?.result;
          if (reportData) setReportDoc(reportData);
          const id = reportData?._id;
          if (id) setReportId(id);
          const referred =
            reportData?.referredBy || reportData?.referred_by ||
            reportData?.referringPhysician || reportData?.referringPhysicianName ||
            extractReferredByFromTemplateHTML(reportData?.templateHTML);
          if (referred && !referredBy) setReferredBy(referred);
          const assignedTo = reportData?.reportAssignedTo || reportData?.assignedTo;
          if (assignedTo) setReportAssignedTo(assignedTo);
        }
      } catch (e) {
        console.error('Failed to resolve report ID from HL7 backend:', e);
      }
    };
    fetchReportId();
  }, [studyInstanceUID]);

  const fetchHistory = useCallback(async () => {
    if (!studyInstanceUID) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const resultAction = await dispatch(
        getDistributionHistoryByStudyApi({ studyInstanceUID, page: 1, limit: 100 })
      );
      if (getDistributionHistoryByStudyApi.fulfilled.match(resultAction)) {
        setDistributions(resultAction.payload?.data ?? []);
      } else {
        setHistoryError((resultAction.payload as string) || 'Failed to load distribution history');
      }
    } catch (e: any) {
      setHistoryError(e?.message || 'Failed to load distribution history');
    } finally {
      setHistoryLoading(false);
    }
  }, [studyInstanceUID, dispatch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDistributionAdded = () => {
    setAddDialogOpen(false);
    setEditDistribution(null);
    fetchHistory();
  };

  const handleEditDraft = (d: DistributionRecord) => {
    setOpenMenuId(null);
    setEditDistribution(d);
    setAddDialogOpen(true);
  };

  const handleSendDraft = async (distributionId: string) => {
    setSendingDraftId(distributionId);
    try {
      await dispatch(sendDraftDistributionApi({ distributionId })).unwrap();
      fetchHistory();
    } catch {
      // Backend may surface via toast elsewhere; list stays in sync on success
    } finally {
      setSendingDraftId(null);
    }
  };

  const handleDeleteDraft = async (distributionId: string) => {
    setDeletingDraftId(distributionId);
    try {
      await dispatch(deleteDraftDistributionApi({ distributionId })).unwrap();
      fetchHistory();
    } catch {
      // ignore
    } finally {
      setDeletingDraftId(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <section
      className="layoutContainer"
      style={{ ...customStyles.layoutBackground, minHeight: '100vh' }}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__reportBody" style={{ borderTop: '1px solid #fff', padding: '1rem 1.5rem' }}>
            <BackButton />
            <div className="mt-2 p-2">
              <div className="reportCard">
                <div className="reportCard__head flex flex-wrap items-center justify-between gap-2">
                  <div className="profile flex items-center gap-2">
                    <span className="text-white">
                      Patient: {studyData?.patientName ?? studyData?.name ?? '—'}
                    </span>
                    <span className="text-white opacity-80">Study: {studyInstanceUID?.slice(-12)}...</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddDialogOpen(true)}
                    disabled={!reportId}
                    className="btn btn__primary"
                  >
                    Add Distribution
                  </button>
                </div>
                {reportStatus != null && !isReportFinalized && (
                  <p className="modal__header--para" style={{ marginBottom: '1rem', marginTop: '0.5rem' }}>
                    Report is not yet finalized. Drafts can be saved but not sent.
                  </p>
                )}
                <div className="mt-4 overflow-x-auto distribution-page__table-wrap">
                  {historyError && (
                    <p className="text-red-400 text-sm mb-2">{historyError}</p>
                  )}
                  <table className="distribution-page__table w-full border-collapse text-left text-white">
                    <thead>
                      <tr className="distribution-page__table-head">
                        <th className="py-3 pr-4 font-semibold">Date</th>
                        <th className="py-3 pr-4 font-semibold">Type</th>
                        <th className="py-3 pr-4 font-semibold">From</th>
                        <th className="py-3 pr-4 font-semibold">To</th>
                        <th className="py-3 pr-4 font-semibold">ACK</th>
                        <th className="py-3 pr-4 font-semibold">Status</th>
                        <th className="py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyLoading ? (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-white opacity-70">
                            Loading...
                          </td>
                        </tr>
                      ) : distributions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-white opacity-70">
                            No distributions yet. Click &quot;Add Distribution&quot; to send this report over Medical Objects or by email.
                          </td>
                        </tr>
                      ) : (
                        distributions.map((d) => (
                          <tr key={d._id} className="distribution-page__table-row">
                            <td className="py-3 pr-4 align-top">{formatDate(d.createdAt)}</td>
                            <td className="py-3 pr-4 align-top">
                              {d.type === 'email' ? 'Email' : 'Medical Objects'}
                            </td>
                            {/* Email has no sending provider identity — it goes out from the
                                platform's no-reply address on the clinic's behalf. Saying so
                                is clearer than a dash in every email row. */}
                            <td className="py-3 pr-4 align-top">
                              {d.type === 'email'
                                ? 'No-reply email'
                                : d.fromProviderName || d.fromProviderId || '—'}
                            </td>
                            {/* An email distribution's recipient IS the address — falling
                                back to the provider fields would show a dash for it. */}
                            <td className="py-3 pr-4 align-top" style={{ wordBreak: 'break-word' }}>
                              {d.type === 'email'
                                ? d.toProviderName
                                  ? `${d.toProviderName} <${d.toEmail || '—'}>`
                                  : d.toEmail || '—'
                                : d.toProviderName || d.toProviderId || '—'}
                            </td>
                            <td className="py-3 pr-4 align-top">{d.ackCode || '—'}</td>
                            <td className="py-3 pr-4 align-top">
                              <span
                                style={{
                                  color: STATUS_COLORS[d.status] ?? '#fff',
                                  fontWeight: 600,
                                }}
                              >
                                {STATUS_LABELS[d.status] ?? d.status}
                              </span>
                            </td>
                            <td className="py-3 align-top">
                              {d.status === 'DRAFT' ? (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    type="button"
                                    disabled={!!sendingDraftId || !!deletingDraftId}
                                    onClick={() => setOpenMenuId(openMenuId === d._id ? null : d._id)}
                                    className="btn btn__white btn__outlined"
                                    style={{
                                      minHeight: '32px',
                                      padding: '0.25rem 0.75rem',
                                      fontSize: '0.8125rem',
                                      lineHeight: '18px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.35rem',
                                    }}
                                  >
                                    {sendingDraftId === d._id
                                      ? 'Sending…'
                                      : deletingDraftId === d._id
                                        ? 'Deleting…'
                                        : 'Actions'}
                                    <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>▾</span>
                                  </button>
                                  {openMenuId === d._id && (
                                    <ul
                                      style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 'calc(100% + 4px)',
                                        zIndex: 20,
                                        minWidth: '140px',
                                        background: '#1a1a1a',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        borderRadius: '0.5rem',
                                        listStyle: 'none',
                                        margin: 0,
                                        padding: '0.25rem 0',
                                        boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                                      }}
                                    >
                                      <li
                                        role="button"
                                        onClick={() => handleEditDraft(d)}
                                        style={{ padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }}
                                      >
                                        Edit
                                      </li>
                                      <li
                                        role="button"
                                        onClick={() => {
                                          if (!isReportFinalized) return;
                                          setOpenMenuId(null);
                                          handleSendDraft(d._id);
                                        }}
                                        title={!isReportFinalized ? 'Report must be finalized to send' : undefined}
                                        style={{
                                          padding: '0.45rem 0.9rem',
                                          cursor: isReportFinalized ? 'pointer' : 'not-allowed',
                                          opacity: isReportFinalized ? 1 : 0.5,
                                          fontSize: '0.85rem',
                                        }}
                                      >
                                        Send
                                      </li>
                                      <li
                                        role="button"
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          handleDeleteDraft(d._id);
                                        }}
                                        style={{ padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', color: '#f87171' }}
                                      >
                                        Delete
                                      </li>
                                    </ul>
                                  )}
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {addDialogOpen && reportId && (
        <AddDistributionDialog
          reportId={reportId}
          patientInfo={patientInfoForDialog}
          editDistribution={editDistribution}
          onClose={() => { setAddDialogOpen(false); setEditDistribution(null); }}
          onSuccess={handleDistributionAdded}
        />
      )}
    </section>
  );
};

export default DistributionPage;
